import styled from '@emotion/styled';
import {
  Box,
  Button,
  Grid,
  Card as MuiCard,
  Typography,
  CircularProgress,
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useBranding } from '../../hooks/api/useBranding';
import { useTrackUserEvent } from '../../hooks/api/useTrackUserEvent';
import {
  useGetI18n,
  useGetAllI18n,
  useUpdateI18n,
  useUpdateI18nCache,
} from '../../hooks/api/useI18n';
import { ErrorObject } from '../../types/errorhandling.types';
import { resizeImage } from '../../utils/imageUtils';
import GeneralTab from './components/GeneralTab/GeneralTab';
import DisplaySettingsTab from './components/DisplaySettingsTab/DisplaySettingsTab';
import { IconsTab } from './components/IconsTab';
import { TabSelect } from './components/TabSelect';
import TextTab from './components/TextTab';
import {
  getAvailableLocales,
  hasTextItems,
  hasIconItems,
} from './utils/i18nHelpers';
import { BrandingResponse } from '../../types/Branding.types';
import { AlertMessage } from '../../components/AlertMessage';
import { PageTitle } from '../../components/PageTitle';
import { env } from '../../env';

const Card = styled(MuiCard)``;

interface BrandingState {
  logoImage: string;
  logoTag: string;
  logoLink: string;
  mainColor: string;
  headerColor: string;
  footerColor: string;
  secondaryColor: string;
  infoBoxColor: string;
  tableHeaderColor: string;
  email: string;
  name: string;
  repositoryUrl: string;
  paymentTitle: string;
  publishingModelUrl: string;
  licensesUrl: string;
  customLicenseUrl: string;
  openInNewWindow: boolean;
  searchTips: boolean;
  showLastUpdatedAt: boolean;
  showSearchSuggestions: boolean;
  openWidgetSearchInNewWindow: boolean;
  accessibilityUrl: string;
  showAgreementDescription: boolean;
  vpatSearch: string;
  vpatAdmin: string;
  columnOrder: string[] | undefined;
}

const Branding = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [fieldErrors, setFieldErrors] = useState<ErrorObject>({});
  const [showToast, setShowToast] = useState(false);
  const [tab, setTab] = useState(() => searchParams.get('tab') || 'general');

  // Consolidated branding state
  const [brandingState, setBrandingState] = useState<BrandingState>({
    logoImage: '',
    logoTag: '',
    logoLink: '',
    mainColor: '',
    headerColor: '',
    footerColor: '',
    secondaryColor: '',
    infoBoxColor: '',
    tableHeaderColor: '',
    email: '',
    name: '',
    repositoryUrl: '',
    paymentTitle: '',
    publishingModelUrl: '',
    licensesUrl: '',
    customLicenseUrl: '',
    openInNewWindow: false,
    searchTips: false,
    showLastUpdatedAt: false,
    showSearchSuggestions: false,
    openWidgetSearchInNewWindow: false,
    accessibilityUrl: '',
    showAgreementDescription: false,
    vpatSearch: '',
    vpatAdmin: '',
    columnOrder: undefined,
  });

  const { branding, updateBranding } = useBranding();
  const { mutateAsync: trackUserEvent } = useTrackUserEvent();
  const { data: i18nApiData, isLoading: isLoadingPrivate } = useGetI18n();
  const { account, impersonationLock } = useAuthContext();
  const { data: publicI18nData, isLoading: isLoadingPublic } =
    useGetAllI18n(account);
  const updateI18nMutation = useUpdateI18n();
  const updateI18nCache = useUpdateI18nCache();
  const publicUrl = env.PUBLIC_SEARCH_URL;

  const mapBrandingToState = useCallback(
    (brandingData: typeof branding): BrandingState => ({
      logoImage: brandingData?.logo_image || '',
      logoTag: brandingData?.logo_tag || '',
      logoLink: brandingData?.logo_link || '',
      mainColor: brandingData?.main_color || '',
      headerColor: brandingData?.header_color || '',
      footerColor: brandingData?.footer_color || '',
      secondaryColor: brandingData?.secondary_color || '',
      infoBoxColor: brandingData?.info_box_color || '',
      tableHeaderColor: brandingData?.table_header_color || '',
      email: brandingData?.email || '',
      name: brandingData?.name || '',
      repositoryUrl: brandingData?.repo_url || '',
      paymentTitle: brandingData?.payment_title || '',
      publishingModelUrl: brandingData?.publishing_model_url || '',
      licensesUrl: brandingData?.licenses_url || '',
      customLicenseUrl: brandingData?.custom_license_url || '',
      openInNewWindow: brandingData?.open_in_new_window ?? false,
      searchTips: brandingData?.search_tips ?? false,
      showLastUpdatedAt: brandingData?.show_last_updated_at ?? false,
      showSearchSuggestions: brandingData?.show_search_suggestions ?? false,
      openWidgetSearchInNewWindow: brandingData?.open_widget_search_in_new_window ?? false,
      accessibilityUrl: brandingData?.accessibility_url ?? '',
      showAgreementDescription: brandingData?.show_agreement_description ?? false,
      vpatSearch: brandingData?.vpat_search ?? '',
      vpatAdmin: brandingData?.vpat_admin ?? '',
      columnOrder: brandingData?.column_order,
    }),
    []
  );

  // Load branding data
  useEffect(() => {
    if (branding) {
      setBrandingState(mapBrandingToState(branding));
    }
  }, [branding, mapBrandingToState]);

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      const resizedImage = await resizeImage(selectedFile);
      setBrandingState((prev) => ({ ...prev, logoImage: resizedImage }));
    }
  };

  const getModifiedBrandingData = () => {
    if (!branding) return {};

    const fieldMapping: Record<keyof BrandingState, string> = {
      logoImage: 'logo_image',
      logoTag: 'logo_tag',
      logoLink: 'logo_link',
      mainColor: 'main_color',
      headerColor: 'header_color',
      footerColor: 'footer_color',
      secondaryColor: 'secondary_color',
      infoBoxColor: 'info_box_color',
      tableHeaderColor: 'table_header_color',
      email: 'email',
      name: 'name',
      repositoryUrl: 'repo_url',
      paymentTitle: 'payment_title',
      publishingModelUrl: 'publishing_model_url',
      licensesUrl: 'licenses_url',
      customLicenseUrl: 'custom_license_url',
      openInNewWindow: 'open_in_new_window',
      searchTips: 'search_tips',
      showLastUpdatedAt: 'show_last_updated_at',
      showSearchSuggestions: 'show_search_suggestions',
      openWidgetSearchInNewWindow: 'open_widget_search_in_new_window',
      accessibilityUrl: 'accessibility_url',
      showAgreementDescription: 'show_agreement_description',
      vpatSearch: 'vpat_search',
      vpatAdmin: 'vpat_admin',
      columnOrder: 'column_order',
    };

    const valuesEqual = (a: unknown, b: unknown) =>
      Array.isArray(a) || Array.isArray(b)
        ? JSON.stringify(a) === JSON.stringify(b)
        : a === b;

    return Object.entries(fieldMapping).reduce(
      (acc, [stateKey, apiKey]) => {
        const currentValue = brandingState[stateKey as keyof BrandingState];
        const originalValue = branding[apiKey as keyof BrandingResponse];

        if (!valuesEqual(currentValue, originalValue)) {
          acc[apiKey] = currentValue;
        }
        return acc;
      },
      {} as Record<string, unknown>
    );
  };

  const handleSave = async () => {
    try {
      if (tab === 'general' || tab === 'display') {
        const modifiedData = getModifiedBrandingData();
        if (Object.keys(modifiedData).length > 0) {
          await updateBranding(modifiedData);
          if (
            'show_search_suggestions' in modifiedData &&
            typeof modifiedData.show_search_suggestions === 'boolean'
          ) {
            await trackUserEvent({
              slug: account,
              eventCode: 'c',
              payload: {
                on: 'suggestions',
                enabled: modifiedData.show_search_suggestions,
              },
            });
          }
        }
      } else if (
        (tab.startsWith('text') || tab.startsWith('icons')) &&
        i18nApiData
      ) {
        const locale = tab.split('-')[1] || 'en';
        const localeData = i18nApiData[locale];

        if (localeData && Object.keys(localeData).length > 0) {
          await updateI18nMutation.mutateAsync({ [locale]: localeData });
        }
      }

      setShowToast(true);
      setTimeout(() => setShowToast(false), 1600);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: ErrorObject } };
      const errors = axiosError.response?.data || (error as ErrorObject);
      setFieldErrors(errors);
      setTimeout(() => setFieldErrors({}), 5000);
    }
  };

  const handleCancel = () => {
    if (branding) {
      setBrandingState(mapBrandingToState(branding));
    }
  };

  const updateBrandingField = (field: keyof BrandingState, value: unknown) => {
    setBrandingState((prev) => ({
      ...prev,
      [field]: value as BrandingState[keyof BrandingState],
    }));
  };

  const handleTabChange = (newTab: string) => {
    setTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Validate and set tab from URL on data load
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && publicI18nData) {
      const availableLocales = getAvailableLocales(publicI18nData);
      const validTabs = [
        'general',
        'display',
        ...availableLocales
          .filter((locale) => hasTextItems(locale, publicI18nData))
          .map((locale: string) =>
            locale === 'en' ? 'text' : `text-${locale}`
          ),
        ...availableLocales
          .filter((locale) => hasIconItems(locale, publicI18nData))
          .map((locale: string) =>
            locale === 'en' ? 'icons' : `icons-${locale}`
          ),
      ];

      if (validTabs.includes(urlTab)) {
        setTab(urlTab);
      } else {
        // Invalid tab in URL, redirect to general
        setTab('general');
        setSearchParams({ tab: 'general' });
      }
    }
  }, [publicI18nData, searchParams, setSearchParams]);

  const getCurrentLocale = (tabValue: string) => {
    const parts = tabValue.split('-');
    return parts[1] || 'en';
  };

  const getCurrentTabIndex = () => {
    // Build the same tabs array as in TabSelect to get the correct index
    if (!publicI18nData) return 0;

    const availableLocales = getAvailableLocales(publicI18nData);
    const tabs = [
      'general',
      'display',
      // Text tabs - only show if locale has string values
      ...availableLocales
        .filter((locale) => hasTextItems(locale, publicI18nData))
        .map((locale: string) => (locale === 'en' ? 'text' : `text-${locale}`)),
      // Icons tabs - only show if locale has hash values
      ...availableLocales
        .filter((locale) => hasIconItems(locale, publicI18nData))
        .map((locale: string) =>
          locale === 'en' ? 'icons' : `icons-${locale}`
        ),
    ];

    const index = tabs.findIndex((tabValue) => tabValue === tab);
    return index >= 0 ? index : 0;
  };

  return (
    <>
      <PageTitle title="Branding" />
      <Card variant="default">
        {showToast && (
          <AlertMessage severity="success" className="mb-4">
            Settings have been changed successfully!
          </AlertMessage>
        )}
        <Grid container justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ xs: 12, md: 8 }}>
            <Typography component="h1" variant="h4" gutterBottom>
              {t('branding.pageTitle')}
            </Typography>
            <Typography variant="body1">
              {t('branding.pageDescription')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 8,
              my: { xs: 16, md: 0 },
            }}
          >
            <Button
              variant="default"
              endIcon={<ClearIcon />}
              disabled={impersonationLock}
              onClick={handleCancel}
            >
              {t('branding.buttons.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={impersonationLock}
              endIcon={<SaveIcon />}
              onClick={handleSave}
            >
              {t('branding.buttons.save')}
            </Button>
          </Box>
        </Grid>
        <Box sx={{ width: '100%' }}>
          {(i18nApiData || publicI18nData) && (
            <TabSelect
              onTabSelect={handleTabChange}
              i18nData={i18nApiData}
              publicI18nData={publicI18nData}
              currentTab={tab}
            />
          )}
          <Box
            role="tabpanel"
            id={`branding-tabpanel-${getCurrentTabIndex()}`}
            aria-labelledby={`branding-tab-${getCurrentTabIndex()}`}
            tabIndex={0}
            sx={{
              mt: { xs: 3, md: 6 },
              mb: { xs: 3, md: 6 },
              px: { xs: 0, sm: 1, md: 2 },
            }}
          >
            {tab === 'general' && (
              <GeneralTab
                data={{
                  name: brandingState.name,
                  logoImage: brandingState.logoImage,
                  logoTag: brandingState.logoTag,
                  logoLink: brandingState.logoLink,
                  repositoryUrl: brandingState.repositoryUrl,
                  openWidgetSearchInNewWindow: brandingState.openWidgetSearchInNewWindow,
                  vpatSearch: brandingState.vpatSearch,
                  vpatAdmin: brandingState.vpatAdmin,
                }}
                colors={{
                  mainColor: brandingState.mainColor,
                  headerColor: brandingState.headerColor,
                  footerColor: brandingState.footerColor,
                  secondaryColor: brandingState.secondaryColor,
                  infoBoxColor: brandingState.infoBoxColor,
                  tableHeaderColor: brandingState.tableHeaderColor,
                }}
                callbacks={{
                  updateField: updateBrandingField,
                  updateColor: updateBrandingField,
                  handleLogoChange: handleLogoChange,
                }}
                readonly={impersonationLock}
                fieldErrors={fieldErrors}
                account={account}
                publicUrl={publicUrl}
                branding={branding}
                i18nData={i18nApiData}
              />
            )}
            {tab === 'display' && (
              <DisplaySettingsTab
                data={{
                  publishingModelUrl: brandingState.publishingModelUrl,
                  licensesUrl: brandingState.licensesUrl,
                  customLicenseUrl: brandingState.customLicenseUrl,
                  accessibilityUrl: brandingState.accessibilityUrl,
                  openInNewWindow: brandingState.openInNewWindow,
                  searchTips: brandingState.searchTips,
                  showLastUpdatedAt: brandingState.showLastUpdatedAt,
                  showAgreementDescription:
                    brandingState.showAgreementDescription,
                  showSearchSuggestions: brandingState.showSearchSuggestions,
                  columnOrder: brandingState.columnOrder,
                }}
                callbacks={{
                  updateField: updateBrandingField,
                }}
                readonly={impersonationLock}
                fieldErrors={fieldErrors}
                branding={branding}
              />
            )}
            {tab.startsWith('text') && (
              <TextTab
                key={`text-${getCurrentLocale(tab)}`}
                currentLocale={getCurrentLocale(tab)}
                i18nData={i18nApiData}
                onI18nDataChange={updateI18nCache}
                fieldErrors={fieldErrors}
              />
            )}
            {tab.startsWith('icons') &&
              (isLoadingPrivate || isLoadingPublic ? (
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <IconsTab
                  key={`icons-${getCurrentLocale(tab)}`}
                  currentLocale={getCurrentLocale(tab)}
                  i18nData={i18nApiData}
                  publicI18nData={publicI18nData}
                  onI18nDataChange={updateI18nCache}
                  fieldErrors={fieldErrors}
                />
              ))}
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default Branding;
